import React, { useMemo, useState } from 'react';
import PropTypes, { InferProps } from 'prop-types';
import { ExpressCheckoutElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeExpressCheckoutElementOptions } from '@stripe/stripe-js';
import { Box, Center, Spinner } from '@chakra-ui/react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';

import { ErrorAlert } from '../../ui-elements/ErrorAlert';
import { FormInputsType, PaymentElementResponse } from '../../../@types';
import { CommonPaymentElementPropTypes } from '../../../propTypes';
import { useTranslations } from '../../../translations/useTranslations';
import { useCommonOptionsStore } from '../../../store';
import { sanitizeErrors } from '../../../utils/errors';
import { SupportedPaymentMethods } from '../../../constants';
import { queryPaymentMethodHash } from '../../../queries/methodHash';

export const GooglePayFormPropTypes = {
  ...CommonPaymentElementPropTypes,
  pm: PropTypes.string.isRequired,
};

export type GooglePayFormProps = InferProps<typeof GooglePayFormPropTypes>;

const layoutProps: Record<string, any> = {
  gap: 6,
  columnGap: 4,
};

const GooglePayForm = React.forwardRef(({
  onLoadStart,
  onLoadEnd,
  onSuccess,
  onError,
  onSubmit,
  onPaymentToken,
  pm,
}: GooglePayFormProps, forwardRef: any) => {
  const stripe = useStripe();
  const elements = useElements();

  const ElementOptions: StripeExpressCheckoutElementOptions = useMemo(() => ({
    layout: {
      maxColumns: 1,
      maxRows: 1,
    },
    paymentMethodOrder: [pm],
    buttonHeight: 40,
    /* @
    buttonTheme: {
      applePay: 'white',
      googlePay: 'white',
    },
    */
    wallets: {
      applePay: pm === SupportedPaymentMethods.APPLEPAY ? 'always' : 'never',
      googlePay: pm === SupportedPaymentMethods.GOOGLEPAY ? 'always' : 'never',
    },
  }), [pm]);

  const lang = useCommonOptionsStore(state => state.lang);
  const propertyId = useCommonOptionsStore(state => state.propertyId);
  const apiKey = useCommonOptionsStore(state => state.apiKey);

  const [t] = useTranslations(lang);

  const formMethods = useForm<FormInputsType>({
    mode: 'onChange',
  });

  const [methodUnavailable, setMethodUnavailable] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>();
  const [isReady, setReady] = useState<boolean>(false);
  const { handleSubmit, formState } = formMethods;

  const {
    errors,
    isLoading,
    isSubmitSuccessful,
    isSubmitting,
  } = formState;

  const errCount = React.useMemo(() => (errors ? Object.keys(errors).length : 0), [errors]);

  React.useEffect(() => {
    if (onLoadStart && isLoading) onLoadStart();
    if (onLoadEnd && isLoading) onLoadEnd();
  }, [isLoading, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onLoadStart && isSubmitting) onLoadStart();
    if (onLoadEnd && isSubmitting) onLoadEnd();
  }, [isSubmitting, onLoadStart, onLoadEnd]);

  React.useEffect(() => {
    if (onSuccess && isSubmitSuccessful) onSuccess();
  }, [isSubmitSuccessful, onSuccess]);

  React.useEffect(() => {
    if (onError && errCount) onError(sanitizeErrors(errors));
  }, [onError, errors, errCount]);

  const submitHandler: SubmitHandler<FormInputsType> = async (formData: any) => {
    const data = { ...formData };

    if (onSubmit) {
      const responseData: PaymentElementResponse = {
        ...data,
        paymentMethod: pm,
      };

      onSubmit({ ...responseData });
    }
  };

  const submitErrorHandler: SubmitHandler<FormInputsType> = async () => {
    if (onError) onError(sanitizeErrors(errors));
  };

  React.useImperativeHandle(forwardRef, () => ({
    submit() {
      handleSubmit(submitHandler, submitErrorHandler)();
    },
    async validate() {
      return true;
    },
  }));

  // @ payment confirmation
  const onConfirm = async () => {
    if (!stripe || !elements) return;

    const { error: submitError } = await elements.submit();
    if (submitError) {
      window.console.log('Submit error.', submitError);
      setErrorMessage(submitError.message || t('paymentMethodError'));
      return;
    }

    const { error, paymentMethod } = await stripe.createPaymentMethod({
      elements,
    });

    // eslint-disable-next-line curly
    if (error) {
      window.console.log('Create payment method failed.', error);
      setErrorMessage(error.message || t('paymentMethodError'));
    }

    // @ hashing payment method
    if (propertyId && apiKey) {
      const {
        hash: hashToken,
        error: hashError,
      } = await queryPaymentMethodHash(+propertyId, apiKey, paymentMethod!.id);
      if (hashToken && onPaymentToken) onPaymentToken(hashToken);
      if (hashError && onError) onError(error);
    }

    let submitResponse: {
      secret: string,
      redirect: string,
    } | null = null;
    if (onSubmit) submitResponse = await onSubmit({ paymentMethod: pm, paymentMethodId: paymentMethod!.id });

    if (!submitResponse?.secret) return;

    const { error: confirmError } = await stripe.confirmPayment({
      clientSecret: submitResponse?.secret,
      confirmParams: {
        payment_method: paymentMethod!.id,
        return_url: submitResponse?.redirect,
      },
    });

    // eslint-disable-next-line curly
    if (confirmError) {
      if (onLoadEnd) onLoadEnd();
      window.console.log('Payment confirmation failed.', confirmError);
      setErrorMessage(confirmError.message || t('paymentMethodError'));
    }
  };

  const onReady = ({ availablePaymentMethods }: any) => {
    setReady(true);
    if (!availablePaymentMethods) {
      setMethodUnavailable(true);
      window.console.log('No available payment methods.');
    } else {
      window.console.log('Express checkout buttons loaded.');
    }
  };

  return (
    <>
      <FormProvider {...formMethods}>
        {!isReady && (
        <Center w="100%" h="100%" p={4}>
          <Spinner
                thickness="4px"
                emptyColor="gray.200"
                color="blue.500"
                size="lg"
              />
        </Center>
        )}
        <Box paddingTop={(layoutProps as any).gap}>
          {errorMessage && (
          <ErrorAlert>
            {errorMessage || t('paymentMethodError')}
          </ErrorAlert>
          )}
          {(methodUnavailable && pm === SupportedPaymentMethods.GOOGLEPAY) && t('googlePayNotAvailableMessage')}
          {(methodUnavailable && pm === SupportedPaymentMethods.APPLEPAY) && t('applePayNotAvailableMessage')}
          <ExpressCheckoutElement
              options={ElementOptions}
              onConfirm={onConfirm}
              onReady={onReady}
            />
        </Box>
      </FormProvider>
    </>
  );
});

GooglePayForm.propTypes = GooglePayFormPropTypes;

export { GooglePayForm };
